/**
 * RAG API
 */

import { RAGQueryRequest, RAGQueryResponse } from '../types/api';
import { getAPIClient, getAPIBaseUrl, getAuthHeaders } from './client';

export async function queryRAG(projectId: string, request: Partial<RAGQueryRequest>): Promise<RAGQueryResponse> {
  const client = getAPIClient();
  // Correct endpoint from API docs: POST /rag/query (project_id in body)
  return client.post<RAGQueryResponse>('/rag/query', {
    project_id: projectId,
    ...request,
  });
}

// Alias for compatibility
export const submitRAGQuery = queryRAG;

/**
 * Streaming RAG Event Types
 */
export interface RAGStreamEvent {
  type: 'status' | 'chunk' | 'citation' | 'complete' | 'error';
  data: RAGStreamStatusData | RAGStreamChunkData | RAGStreamCitationData | RAGStreamCompleteData | RAGStreamErrorData;
}

export interface RAGStreamStatusData {
  stage: 'started' | 'context' | 'retrieval' | 'retrieval_complete' | 'generating' | 'thinking' | 'complete' | 'no_evidence';
  message: string;
  elapsed?: number;
  evidence_count?: number;
  retrieval_time?: number;
  thinking_time?: number;
  reasoning_tokens?: number;
  output_tokens?: number;
}

export interface RAGStreamChunkData {
  content: string;
  total_length: number;
}

export interface RAGStreamCitationData {
  index: number;
  source: string;
  page?: number;
  section?: string;
  confidence: number;
  artifact_id?: string;
  chunk_id?: string;
  content?: string;
}

export interface RAGStreamCompleteData {
  answer: string;
  citations: Array<{
    index: number;
    source: string;
    page?: number;
    section?: string;
    content?: string;
    confidence: number;
    artifact_id?: string;
    chunk_id?: string;
  }>;
  status: string;
  confidence?: number;
  retrieval_time?: number;
  generation_time?: number;
  total_time?: number;
  evidence_count?: number;
  /** Server-generated request id for correlating stream with logs (each request gets a unique id). */
  request_id?: string;
}

export interface RAGStreamErrorData {
  message: string;
  code: string;
  elapsed?: number;
}

/**
 * Streaming RAG Query using Server-Sent Events (SSE)
 * 
 * @param projectId - Project UUID
 * @param request - Query parameters
 * @param callbacks - Event callbacks for each event type
 * @returns AbortController to cancel the stream
 */
export function queryRAGStream(
  projectId: string,
  request: Partial<RAGQueryRequest>,
  callbacks: {
    onStatus?: (data: RAGStreamStatusData) => void;
    onChunk?: (data: RAGStreamChunkData) => void;
    onCitation?: (data: RAGStreamCitationData) => void;
    onComplete?: (data: RAGStreamCompleteData) => void;
    onError?: (data: RAGStreamErrorData) => void;
  }
): AbortController {
  const controller = new AbortController();
  
  // Start the stream in the background
  (async () => {
    try {
      const baseUrl = getAPIBaseUrl();
      const headers = getAuthHeaders();
      
      const response = await fetch(`${baseUrl}/rag/query/stream`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({
          project_id: projectId,
          ...request,
        }),
        signal: controller.signal,
      });
      
      if (!response.ok) {
        const error = await response.json();
        callbacks.onError?.({
          message: error.detail || 'Stream request failed',
          code: 'HTTP_ERROR',
        });
        return;
      }
      
      const reader = response.body?.getReader();
      if (!reader) {
        callbacks.onError?.({ message: 'No response body', code: 'NO_BODY' });
        return;
      }
      
      const decoder = new TextDecoder();
      let buffer = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        let currentEvent = '';
        let currentData = '';
        
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            currentData = line.slice(6);
          } else if (line === '' && currentEvent && currentData) {
            // End of event, process it
            try {
              const data = JSON.parse(currentData);
              
              switch (currentEvent) {
                case 'status':
                  callbacks.onStatus?.(data as RAGStreamStatusData);
                  break;
                case 'chunk':
                  callbacks.onChunk?.(data as RAGStreamChunkData);
                  break;
                case 'citation':
                  callbacks.onCitation?.(data as RAGStreamCitationData);
                  break;
                case 'complete':
                  callbacks.onComplete?.(data as RAGStreamCompleteData);
                  break;
                case 'error':
                  callbacks.onError?.(data as RAGStreamErrorData);
                  break;
              }
            } catch (e) {
              console.error('[RAG Stream] Failed to parse event:', e, currentData);
            }
            
            currentEvent = '';
            currentData = '';
          }
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('[RAG Stream] Aborted by user');
        return;
      }
      callbacks.onError?.({
        message: error.message || 'Stream error',
        code: 'STREAM_ERROR',
      });
    }
  })();
  
  return controller;
}

export async function assembleDraft(
  projectId: string,
  sections: Array<{ heading: string; content: string }>
): Promise<{ draft: string; citations: any[] }> {
  const client = getAPIClient();
  return client.post('/rag/assemble', {
    project_id: projectId,
    sections,
  });
}

export async function validateEvidence(
  projectId: string,
  evidenceIds: string[]
): Promise<{ valid: number; invalid: number; details: any[] }> {
  const client = getAPIClient();
  return client.post('/rag/validate', {
    project_id: projectId,
    evidence_ids: evidenceIds,
  });
}

